import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useDispatch } from "react-redux";
import { login } from "../features/auth/authSlice";
import AuthCard from "./auth/AuthCard";
import Notice from "./ui/Notice";
import { Button, Field, Input } from "../designSystem";
import {
  checkPasswordMessage,
  goodPassword,
  setPodcastSession,
  setToken,
  setUser,
} from "../utils";
import { getFullApiUrl, podcastId } from "../config";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const dispatch = useDispatch();

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("error"); // "error" or "success"
  const [isLoading, setIsLoading] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 验证token
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await axios.post(
          getFullApiUrl("/api/v1/auth/verify_reset_password_token"),
          {
            token: token,
          }
        );

        if (response.status === 200 && response.data.email) {
          setIsValidToken(true);
          setEmail(response.data.email);
        } else {
          setIsValidToken(false);
          setMessage("重置链接无效或已过期");
          setMessageType("error");
        }
      } catch (err) {
        console.error("Token verification error:", err);
        setIsValidToken(false);
        setMessage("重置链接无效或已过期");
        setMessageType("error");
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setIsLoading(false);
      setIsValidToken(false);
      setMessage("重置链接无效");
      setMessageType("error");
    }
  }, [token]);

  // 检查密码强度
  useEffect(() => {
    setPasswordMessage(checkPasswordMessage(password));
  }, [password]);

  // 检查密码匹配
  useEffect(() => {
    if (confirmPassword && password !== confirmPassword) {
      setMessage("密码不匹配");
      setMessageType("error");
    } else {
      setMessage("");
    }
  }, [password, confirmPassword]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (passwordMessage !== goodPassword) {
      setMessage("密码不符合安全要求");
      setMessageType("error");
      return;
    }

    if (password !== confirmPassword) {
      setMessage("密码不匹配");
      setMessageType("error");
      return;
    }

    setIsSubmitting(true);
    setMessage("");

    try {
      const response = await axios.post(
        getFullApiUrl("/api/v1/auth/reset_password"),
        {
          token: token,
          password: password,
          podcast_id: podcastId,
        }
      );

      if (response.status === 200 && response.data.token) {
        // 保存登录信息
        setToken(response.data.token);
        if (response.data.podcast_session) {
          setPodcastSession(response.data.podcast_session);
        }
        setUser(JSON.stringify(response.data.user));
        dispatch(
          login({
            user: response.data.user,
            token: response.data.token,
          })
        );

        setMessage("密码重置成功，正在跳转...");
        setMessageType("success");

        // 跳转到首页
        setTimeout(() => {
          navigate("/");
        }, 200);
      } else {
        setMessage(response.data.error || "密码重置失败，请稍后重试");
        setMessageType("error");
      }
    } catch (err) {
      console.error("Reset password error:", err);
      const errorMessage =
        err.response?.data?.error || "密码重置失败，请稍后重试";
      setMessage(errorMessage);
      setMessageType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="lt-center-state">
        <span className="lt-loading-dot" aria-hidden="true" />
        <p>验证重置链接…</p>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <AuthCard title="重置链接无效">
        <Notice variant="error">{message}</Notice>
        <Button size="lg" fullWidth onClick={() => navigate("/forgot")}>
          重新获取重置链接
        </Button>
      </AuthCard>
    );
  }

  const passwordOk = passwordMessage === goodPassword;

  return (
    <AuthCard title="重置密码" subtitle="请为您的账户设置新密码。">
      <form onSubmit={handleSubmit} noValidate style={{ display: "grid", gap: 16 }}>
        <div className="lt-auth-email">{email}</div>
        <Field
          label="新密码"
          htmlFor="password"
          error={password && !passwordOk ? passwordMessage : null}
          hint={password && passwordOk ? passwordMessage : null}
        >
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            autoFocus
            error={!!password && !passwordOk}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
          />
        </Field>
        <Field label="确认新密码" htmlFor="confirmPassword">
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            error={!!confirmPassword && confirmPassword !== password}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isSubmitting}
          />
        </Field>
        {message ? (
          <Notice variant={messageType === "success" ? "success" : "error"}>
            {message}
          </Notice>
        ) : null}
        <Button
          type="submit"
          size="lg"
          fullWidth
          loading={isSubmitting}
          disabled={
            isSubmitting ||
            !passwordOk ||
            password !== confirmPassword ||
            !password ||
            !confirmPassword
          }
        >
          {isSubmitting ? "重置中" : "重置密码"}
        </Button>
      </form>
    </AuthCard>
  );
}
